import { CloseoutChecklist } from './JobWorkspace';
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemContent,
  IconButton,
  Select,
  Option,
  Card,
} from '@mui/joy';
import { FileText, PaperPlaneTilt as Send, DownloadSimple as Download, Image, CurrencyDollar as DollarSign, Paperclip, FileText as FileCheck, Trash as Trash2, Play } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import type { Job } from '../../types';
import { api } from '../../lib/api';

interface SupplementReportModalProps {
  open: boolean;
  onClose: () => void;
  job: Job;
}

interface PhotoBulkUpdateResponse {
  updated_count: number;
}

interface SupplementRequest {
  id: number;
  job: number;
  job_number: string;
  job_address: string;
  status: 'draft' | 'sent' | 'completed' | 'follow_up_needed';
  status_display: string;
  selected_estimate: number;
  selected_estimate_details: {
    id: number;
    document_number: string;
    total_amount: string;
    status: string;
    customer_name: string;
    created_at: string;
  } | null;
  email_subject: string;
  email_body: string;
  recipient_email: string;
  pdf_url?: string;
  generation_status?: 'pending' | 'processing' | 'completed' | 'failed';
  generation_error?: string;
  celery_task_id?: string;
  sent_at?: string;
  next_followup_at?: string;
  created_at: string;
  is_async?: boolean;
  message?: string;
}

interface Estimate {
  id: number;
  document_number: string;
  total_amount: string;
  status: string;
  customer_detail: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  created_at: string;
}

interface PhotoWithDescription {
  id: number;
  image_url: string;
  description?: string;
  supplement_description?: string;
}

interface AttachmentInfo {
  type: 'photo' | 'document';
  name: string;
  count: number;
  items?: Array<{
    id: number;
    filename: string;
  }>;
}

export const SupplementReportModal: React.FC<SupplementReportModalProps> = ({ open, onClose, job }) => {
  const queryClient = useQueryClient();
  const [activeRequest, setActiveRequest] = useState<SupplementRequest | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<number | null>(null);
  const [currentStep, setCurrentStep] = useState(1); // 1: Select Estimate, 2: Photo Descriptions, 3: Review & Send
  const [selectedEstimateId, setSelectedEstimateId] = useState<number | null>(null);
  const [photoDescriptions, setPhotoDescriptions] = useState<{[key: number]: string}>({});
  const [formData, setFormData] = useState({
    email_subject: '',
    email_body: '',
    recipient_email: '',
  });
  const [attachments, setAttachments] = useState<AttachmentInfo[]>([]);

  // Fetch existing Supplement requests for this job
  const { data: existingRequests = [], isLoading: requestsLoading } = useQuery({
    queryKey: ['supplement-requests', job.id],
    queryFn: async () => {
      const response = await api.getAllPages<any>(`/api/supplement-requests/?job_id=${job.id}`);
      if (response.status !== 200) throw new Error('Failed to fetch supplement requests');
      const data = response.data;
      return data;
    },
    enabled: open,
  });

  // Fetch estimates for this job
  const { data: estimates = [], isLoading: estimatesLoading } = useQuery({
    queryKey: ['estimates', job.id],
    queryFn: async () => {
      const response = await api.getAllPages<any>(`/api/estimates/?job=${job.id}`);
      if (response.status !== 200) throw new Error('Failed to fetch estimates');
      const data = response.data;
      return data;
    },
    enabled: open,
  });

  // Fetch supplement-tagged photos for step 2
  const { data: supplementPhotos = [], isLoading: photosLoading } = useQuery({
    queryKey: ['supplement-photos', job.id],
    queryFn: async () => {
      // Fetch all photos to ensure we don't miss any supplement photos due to pagination
      let allPhotos: any[] = [];
      let page = 1;
      let hasNext = true;
      
      while (hasNext) {
        const response = await api.get<any>(`/api/photos/by-job/${job.id}/?page=${page}`);
        if (response.status !== 200) throw new Error('Failed to fetch photos');
        
        const data = response.data;
        if (Array.isArray(data)) {
          // Direct array response (no pagination)
          allPhotos = data;
          hasNext = false;
        } else {
          // Paginated response
          const photos = data.results || [];
          allPhotos = allPhotos.concat(photos);
          hasNext = !!data.next;
          page++;
        }
      }
      
      // Filter for supplement-tagged photos
      const supplementFilteredPhotos = allPhotos.filter((photo: any) => 
        photo.tags?.some((tag: any) => tag.name === 'supplement')
      );
      
      console.log(`Found ${allPhotos.length} total photos, ${supplementFilteredPhotos.length} supplement-tagged photos`);
      return supplementFilteredPhotos;
    },
    enabled: open && currentStep >= 2,
  });

  // Fetch supplement-tagged documents
  useEffect(() => {
    if (open && currentStep >= 2) {
      fetchAttachmentInfo();
    }
  }, [open, currentStep]);

  const fetchAttachmentInfo = async () => {
    try {
      const newAttachments: AttachmentInfo[] = [];
      
      // Fetch documents with 'supplement' tag
      const docsResponse = await api.getAllPages<any>(`/api/docs/?job=${job.id}`);
      if (docsResponse.status === 200) {
        const docsData = docsResponse.data;
        const docs = docsData;
        const supplementDocs = docs.filter((doc: any) => 
          doc.tags?.some((tag: any) => tag.name === 'supplement')
        );
        
        if (supplementDocs.length > 0) {
          newAttachments.push({
            type: 'document',
            name: 'Documents tagged "supplement"',
            count: supplementDocs.length,
            items: supplementDocs.map((doc: any) => ({
              id: doc.id,
              filename: doc.file_url?.split('/').pop() || doc.file_name || `Document ${doc.id}`
            }))
          });
        }
      }
      
      setAttachments(newAttachments);
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  };

  // Create Supplement request mutation
  const createRequestMutation = useMutation({
    mutationFn: async (data: { selected_estimate: number }) => {
      const response = await api.post<SupplementRequest>(`/api/jobs/${job.id}/supplement-request/`, data);

      if (response.status !== 201 && response.status !== 200 && response.status !== 202) {
        const error = response.data as any;
        throw new Error(error.error || 'Failed to create supplement request');
      }

      return response.data;
    },
    onSuccess: (data) => {
      setActiveRequest(data);
      setFormData({
        email_subject: data.email_subject,
        email_body: data.email_body,
        recipient_email: data.recipient_email,
      });
      
      // Move to step 2 (photo descriptions)
      setCurrentStep(2);
      
      queryClient.invalidateQueries({ queryKey: ['supplement-requests', job.id] });
    },
  });

  // Send email mutation with async PDF generation support
  const sendEmailMutation = useMutation({
    mutationFn: async () => {
      if (!activeRequest) throw new Error('No active request');

      // Show toast for PDF generation start
      toast.loading('Starting PDF generation with your photo descriptions...', { id: 'pdf-generation' });

      const response = await api.post<any>(`/api/supplement-requests/${activeRequest.id}/send/`, {
        email_subject: formData.email_subject,
        email_body: formData.email_body,
        recipient_email: formData.recipient_email,
      });

      // Handle async response (202) vs sync response (200/201)
      if (response.status === 202 && response.data.is_async) {
        // Async generation started - show different message and poll for completion
        toast.dismiss('pdf-generation');
        toast.loading('Generating PDF in background...', { id: 'pdf-async' });
        
        // Start polling for completion
        return pollForPdfCompletion(activeRequest.id);
      } else if (response.status === 200 || response.status === 201) {
        // Sync completion (fallback)
        toast.dismiss('pdf-generation');
        return response.data;
      } else {
        const error = response.data as any;
        throw new Error(error.error || 'Failed to send email');
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['supplement-requests', job.id] });
      toast.dismiss('pdf-async');
      
      if (data.email_sent) {
        onClose();
        toast.success('Supplement report generated and sent successfully!');
      } else {
        toast.success('Supplement report generated successfully!');
      }
    },
    onError: (error: any) => {
      toast.dismiss('pdf-generation');
      toast.dismiss('pdf-async');
      toast.error(error.message || 'Failed to generate supplement report');
    },
  });

  // Function to poll for PDF completion
  const pollForPdfCompletion = async (requestId: number): Promise<any> => {
    const maxAttempts = 60; // Poll for up to 5 minutes (every 5 seconds)
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
      attempts++;
      
      try {
        const statusResponse = await api.get(`/api/supplement-requests/${requestId}/status/`);
        const status = statusResponse.data as any;
        
        if (status.generation_status === 'completed') {
          // PDF completed successfully
          return {
            status: 'success',
            email_sent: true, // Assume email was sent by the task
            pdf_url: status.pdf_url
          };
        } else if (status.generation_status === 'failed') {
          // PDF generation failed
          throw new Error(status.error || 'PDF generation failed');
        }
        // If still processing, continue polling
        
      } catch (error) {
        console.error('Error polling PDF status:', error);
        throw new Error('Failed to check PDF generation status');
      }
    }
    
    // Timeout
    throw new Error('PDF generation timed out. Please try again later.');
  };

  // Delete supplement request mutation
  const deleteRequestMutation = useMutation({
    mutationFn: async (requestId: number) => {
      const response = await api.delete(`/api/supplement-requests/${requestId}/`);
      
      if (response.status !== 204 && response.status !== 200) {
        const error = response.data as any;
        throw new Error(error.error || 'Failed to delete supplement request');
      }
      
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplement-requests', job.id] });
      toast.success('Draft supplement request deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete supplement request');
    },
  });

  // Save photo descriptions mutation
  const saveDescriptionsMutation = useMutation<PhotoBulkUpdateResponse, Error, Record<number, string>>({
    mutationFn: async (descriptions: Record<number, string>) => {
      const response = await api.patch(`/api/photos/bulk-update-supplement-descriptions/`, {
        photo_descriptions: descriptions
      });

      if (response.status !== 200) {
        const error = response.data as any;
        throw new Error(error.error || 'Failed to save photo descriptions');
      }

      return response.data as PhotoBulkUpdateResponse;
    },
    onSuccess: (data) => {
      console.log('Photo descriptions saved:', data);
      toast.success(`Saved descriptions for ${data.updated_count} photos`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save photo descriptions');
    },
  });

  const handleCreateRequest = () => {
    if (!selectedEstimateId) {
      toast.error('Please select an estimate');
      return;
    }
    
    createRequestMutation.mutate({
      selected_estimate: selectedEstimateId,
    });
  };

  const handleNextStep = async () => {
    if (currentStep === 2) {
      // Save photo descriptions before moving to step 3
      try {
        // Filter out empty descriptions
        const descriptionsToSave = Object.fromEntries(
          Object.entries(photoDescriptions).filter(([_, description]) => description.trim() !== '')
        );
        
        if (Object.keys(descriptionsToSave).length > 0) {
          await saveDescriptionsMutation.mutateAsync(descriptionsToSave);
        }
        
        setCurrentStep(3);
      } catch (error) {
        // Error is handled by the mutation's onError
        console.error('Failed to save photo descriptions:', error);
      }
    }
  };

  const handleSendEmail = () => {
    if (!formData.recipient_email) {
      toast.error('Please enter a recipient email address');
      return;
    }
    sendEmailMutation.mutate();
  };

  const handleContinueRequest = (request: SupplementRequest) => {
    // Load the existing request data
    setActiveRequest(request);
    setSelectedEstimateId(request.selected_estimate);
    setFormData({
      email_subject: request.email_subject,
      email_body: request.email_body,
      recipient_email: request.recipient_email,
    });

    // Determine which step to show based on completion status
    // For now, always go to step 2 (photo descriptions)
    setCurrentStep(2);
  };

  const handleDeleteRequest = (requestId: number) => {
    if (window.confirm('Are you sure you want to delete this draft supplement request? This action cannot be undone.')) {
      deleteRequestMutation.mutate(requestId);
    }
  };

  // A supplement is negotiated with the adjuster, so they are the default
  // recipient (same rule the backend applies), with the homeowner as the
  // fallback. Anything already typed is left alone.
  useEffect(() => {
    const defaultRecipient = job.adjuster_email || job.associated_contact?.email;
    if (open && !formData.recipient_email && defaultRecipient) {
      setFormData(prev => ({
        ...prev,
        recipient_email: defaultRecipient,
      }));
    }
  }, [open, job.adjuster_email, job.associated_contact]);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setActiveRequest(null);
      setCurrentStep(1);
      setSelectedEstimateId(null);
      setPhotoDescriptions({});
      setFormData({
        email_subject: '',
        email_body: '',
        recipient_email: '',
      });
      setAttachments([]);
      setIsPolling(false);
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
  }, [open, pollingInterval]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Box>
            <Typography level="body-md" sx={{ mb: 3 }}>
              Select an estimate to include in the supplement report for job {job.job_number}.
            </Typography>

            <FormControl required>
              <FormLabel>Select Estimate</FormLabel>
              {estimatesLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                  <CircularProgress size="sm" />
                  <Typography level="body-sm">Loading estimates...</Typography>
                </Box>
              ) : estimates.length === 0 ? (
                <Alert color="warning" variant="soft">
                  No estimates found for this job. Please create an estimate first.
                </Alert>
              ) : (
                <Select
                  value={selectedEstimateId}
                  onChange={(_, value) => setSelectedEstimateId(value as number)}
                  placeholder="Choose an estimate..."
                >
                  {estimates.map((estimate: Estimate) => (
                    <Option key={estimate.id} value={estimate.id}>
                      {estimate.document_number || `EST-${estimate.id}`} - ${parseFloat(estimate.total_amount || '0').toFixed(2)} ({estimate.status})
                    </Option>
                  ))}
                </Select>
              )}
            </FormControl>

            {selectedEstimateId && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                {(() => {
                  const selectedEst = estimates.find((e: Estimate) => e.id === selectedEstimateId);
                  return selectedEst ? (
                    <Box>
                      <Typography level="title-sm" sx={{ mb: 1 }}>Selected Estimate Details:</Typography>
                      <Typography level="body-sm">Number: {selectedEst.document_number || `EST-${selectedEst.id}`}</Typography>
                      <Typography level="body-sm">Amount: ${parseFloat(selectedEst.total_amount || '0').toFixed(2)}</Typography>
                      <Typography level="body-sm">Customer: {selectedEst.customer_detail ? `${selectedEst.customer_detail.first_name} ${selectedEst.customer_detail.last_name}` : 'N/A'}</Typography>
                      <Typography level="body-sm">Status: {selectedEst.status}</Typography>
                    </Box>
                  ) : null;
                })()}
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography level="body-md" sx={{ mb: 3 }}>
              Add descriptions for photos that will be included in the supplement report.
            </Typography>

            {photosLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                <CircularProgress size="sm" />
                <Typography level="body-sm">Loading photos...</Typography>
              </Box>
            ) : supplementPhotos.length === 0 ? (
              <Alert color="warning" variant="soft">
                No photos tagged with "supplement" found. Please tag photos with "supplement" first.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {supplementPhotos.map((photo: PhotoWithDescription) => (
                  <Box key={photo.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <Box sx={{ flexShrink: 0 }}>
                        <img
                          src={photo.image_url}
                          alt="Photo"
                          style={{
                            width: '100px',
                            height: '80px',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography level="title-sm" sx={{ mb: 1 }}>Photo {photo.id}</Typography>
                        <FormControl>
                          <FormLabel>Description for Supplement Report</FormLabel>
                          <Textarea
                            placeholder="Enter description for this photo..."
                            value={photoDescriptions[photo.id] || photo.supplement_description || photo.description || ''}
                            onChange={(e) => setPhotoDescriptions(prev => ({
                              ...prev,
                              [photo.id]: e.target.value
                            }))}
                            minRows={2}
                          />
                        </FormControl>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Attachment Info */}
            {attachments.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography level="title-sm" sx={{ mb: 2 }}>
                  Additional attachments to be included:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {attachments.map((attachment, index) => (
                    <Box key={index}>
                      <Typography level="body-sm" sx={{ mb: 1, fontWeight: 600 }}>
                        <FileText size={16} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                        {attachment.name} ({attachment.count})
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography level="body-md" sx={{ mb: 3 }}>
              Review and configure email details before sending the supplement report.
            </Typography>

            <Alert color="success" variant="soft" sx={{ mb: 3 }}>
              Supplement report is ready! Review the email details below before sending.
            </Alert>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl required>
                <FormLabel>Recipient Email</FormLabel>
                <Input
                  type="email"
                  value={formData.recipient_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipient_email: e.target.value }))}
                  placeholder="customer@example.com"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Email Subject</FormLabel>
                <Input
                  value={formData.email_subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, email_subject: e.target.value }))}
                  placeholder="Supplement Report"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Email Body</FormLabel>
                <Textarea
                  value={formData.email_body}
                  onChange={(e) => setFormData(prev => ({ ...prev, email_body: e.target.value }))}
                  minRows={8}
                  placeholder="Enter the email message..."
                />
              </FormControl>
            </Box>

            {/* Summary of what will be included */}
            <Box sx={{ mt: 3 }}>
              <Typography level="title-sm" sx={{ mb: 2 }}>
                Report will include:
              </Typography>
              <List size="sm">
                <ListItem>
                  <ListItemContent>
                    <Typography level="body-sm">
                      <FileCheck size={16} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                      Title page with job information
                    </Typography>
                  </ListItemContent>
                </ListItem>
                <ListItem>
                  <ListItemContent>
                    <Typography level="body-sm">
                      <DollarSign size={16} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                      Selected estimate: {activeRequest?.selected_estimate_details?.document_number || 'N/A'}
                    </Typography>
                  </ListItemContent>
                </ListItem>
                <ListItem>
                  <ListItemContent>
                    <Typography level="body-sm">
                      <Image size={16} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                      {supplementPhotos.length} photo(s) with descriptions
                    </Typography>
                  </ListItemContent>
                </ListItem>
                {attachments.map((attachment, index) => (
                  <ListItem key={index}>
                    <ListItemContent>
                      <Typography level="body-sm">
                        <Paperclip size={16} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                        {attachment.count} document(s)
                      </Typography>
                    </ListItemContent>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg" sx={{ maxWidth: 800 }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileText />
            Supplement Report
          </Box>
        </DialogTitle>

        <DialogContent>
          <CloseoutChecklist job={job} kind="supplement" recipient={formData.recipient_email} />
          {/* Progress indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: step <= currentStep ? 'primary.500' : 'neutral.200',
                    color: step <= currentStep ? 'white' : 'neutral.500',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {step}
                </Box>
                {step < 3 && (
                  <Box
                    sx={{
                      flex: 1,
                      height: 2,
                      backgroundColor: step < currentStep ? 'primary.500' : 'neutral.200',
                      mx: 1
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </Box>

          {/* Step labels */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography level="body-xs" sx={{ color: currentStep >= 1 ? 'primary.500' : 'neutral.500' }}>
              Select Estimate
            </Typography>
            <Typography level="body-xs" sx={{ color: currentStep >= 2 ? 'primary.500' : 'neutral.500' }}>
              Photo Descriptions
            </Typography>
            <Typography level="body-xs" sx={{ color: currentStep >= 3 ? 'primary.500' : 'neutral.500' }}>
              Review & Send
            </Typography>
          </Box>

          {/* Existing Supplement Requests */}
          {existingRequests.length > 0 && currentStep === 1 && (
            <Box sx={{ mb: 3 }}>
              <Typography level="title-sm" sx={{ mb: 2 }}>
                Existing supplement requests for this job
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {existingRequests.map((req: SupplementRequest) => (
                  <Card key={req.id} variant="soft" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip 
                            size="sm" 
                            color={req.status === 'draft' ? 'neutral' : req.status === 'sent' ? 'success' : 'warning'}
                            variant="soft"
                          >
                            {req.status_display}
                          </Chip>
                          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            Created {new Date(req.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                        
                        <Typography level="body-sm" sx={{ mb: 1 }}>
                          <strong>Estimate:</strong> {req.selected_estimate_details?.document_number || 'N/A'}
                          {req.selected_estimate_details?.total_amount && 
                            ` - $${parseFloat(req.selected_estimate_details.total_amount).toFixed(2)}`
                          }
                        </Typography>
                        
                        {req.sent_at && (
                          <Typography level="body-sm" sx={{ color: 'success.main' }}>
                            Sent {new Date(req.sent_at).toLocaleDateString()}
                          </Typography>
                        )}
                        
                        <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 1 }}>
                          To: {req.recipient_email || 'No recipient set'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 2 }}>
                        {/* Continue button - only for drafts */}
                        {req.status === 'draft' && (
                          <IconButton
                            size="sm"
                            variant="outlined"
                            color="primary"
                            onClick={() => handleContinueRequest(req)}
                            title="Continue editing this request"
                          >
                            <Play size={16} />
                          </IconButton>
                        )}
                        
                        {/* Download button - only if PDF exists */}
                        {req.pdf_url && (
                          <IconButton
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            component="a"
                            href={req.pdf_url}
                            target="_blank"
                            title="Download PDF"
                          >
                            <Download size={16} />
                          </IconButton>
                        )}
                        
                        {/* Delete button - only for drafts */}
                        {req.status === 'draft' && (
                          <IconButton
                            size="sm"
                            variant="outlined"
                            color="danger"
                            onClick={() => handleDeleteRequest(req.id)}
                            disabled={deleteRequestMutation.isPending}
                            title="Delete this draft request"
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography level="title-sm" sx={{ mb: 2 }}>
                Create new supplement request
              </Typography>
            </Box>
          )}

          {renderStepContent()}
        </DialogContent>

        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          
          {/* Back button for steps 2 and 3 when continuing an existing request */}
          {(currentStep === 2 || currentStep === 3) && activeRequest && (
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => setCurrentStep(currentStep - 1)}
            >
              Back
            </Button>
          )}
          
          {currentStep === 1 && (
            <Button
              variant="solid"
              color="primary"
              onClick={handleCreateRequest}
              loading={createRequestMutation.isPending}
              disabled={!selectedEstimateId || estimates.length === 0}
            >
              Next: Photo Descriptions
            </Button>
          )}
          
          {currentStep === 2 && (
            <Button
              variant="solid"
              color="primary"
              onClick={handleNextStep}
              loading={saveDescriptionsMutation.isPending}
              disabled={supplementPhotos.length === 0}
            >
              Next: Review & Send
            </Button>
          )}
          
          {currentStep === 3 && (
            <Button
              variant="solid"
              color="primary"
              startDecorator={<Send />}
              onClick={handleSendEmail}
              loading={sendEmailMutation.isPending}
              disabled={!formData.recipient_email}
            >
              {sendEmailMutation.isPending ? 'Generating PDF & Sending...' : 'Generate PDF & Send Email'}
            </Button>
          )}
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};